import React from 'react'
import { TouchableOpacity, View } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { Divider } from '@ui-kitten/components'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Icon, Pill, Text } from '@components/base'
import { Maybe, ProductMin, Subscription, UhcStoreSubscription, UiPillType } from '@src/types'
import { isSubscriptionActive } from '@src/hooks/useActiveSubscriptions.ts'
import { FlexView } from '@components'
import { useLazyScreenPreload } from '@src/navigation/utils'

interface ProductSummaryProps {
  product: Pick<ProductMin, 'title'>
  subscription?: Pick<Subscription, 'id' | 'status' | 'cancelAt'> & {
    uhcStoreSubscription?: Maybe<Pick<UhcStoreSubscription, 'id'>>
    primaryProduct: {
      uncancelable: boolean
    }
  }
  pillLabel: string
  pillStatus: UiPillType
}

export const PurchasedProductSummary = ({
  product,
  subscription,
  pillLabel,
  pillStatus,
}: ProductSummaryProps) => {
  const styles = useStyleSheet(themedStyle)
  const navigation = useNavigation()

  const showPill = subscription ? isSubscriptionActive(subscription) : true

  useLazyScreenPreload('ManageUpcomingSubscriptionModal', {
    shouldPreload: !subscription,
  })
  useLazyScreenPreload('ReactivateSubscriptionModal', {
    shouldPreload: subscription?.cancelAt && subscription.primaryProduct.uncancelable,
  })

  const onProductDetailsPress = () => {
    if (subscription) {
      navigation.navigate('ProgramDetails', { subscriptionId: subscription.id })
    } else {
      navigation.navigate('ManageUpcomingSubscriptionModal')
    }
  }

  const onReactivateSubscriptionPress = () => {
    if (!subscription) {
      return
    }

    navigation.navigate('ReactivateSubscriptionModal', { subscriptionId: subscription.id })
  }

  return (
    <>
      <View style={styles.section}>
        <View style={styles.row}>
          <FlexView>
            <Text type="small" style={styles.textSecondary}>
              {subscription ? 'Current' : 'Upcoming'} Plan
            </Text>

            <Text type="large" bold>
              {product.title}
            </Text>
          </FlexView>

          {showPill && <Pill text={pillLabel} status={pillStatus} />}
        </View>
        {subscription && subscription.cancelAt && subscription.primaryProduct.uncancelable && (
          <Button
            size="small"
            type="outline"
            accessibilityLabel="Reactivate Subscription"
            onPress={onReactivateSubscriptionPress}
            style={styles.reactivateSubscriptionButton}
          >
            Reactivate Subscription
          </Button>
        )}
      </View>

      <Divider style={styles.divider} />

      <TouchableOpacity accessibilityLabel="Show product details" onPress={onProductDetailsPress}>
        <View style={[styles.section, styles.row, { height: 64 }]}>
          <Text type="regular" bold style={styles.manageText}>
            Show product details
          </Text>
          <Icon name="caret-right" style={styles.arrowIcon} />
        </View>
      </TouchableOpacity>
    </>
  )
}

const themedStyle = StyleService.create({
  section: {
    paddingVertical: 12,
    justifyContent: 'center',
  },
  row: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  textSecondary: {
    color: 'theme.text.secondary',
  },
  manageText: {
    color: 'theme.text.link',
  },
  arrowIcon: {
    width: 16,
    height: 16,
    color: 'theme.surface.base0',
  },
  divider: {
    backgroundColor: 'theme.surface.base2',
    height: 2,
  },
  reactivateSubscriptionButton: {
    width: '100%',
    marginTop: 16,
  },
})
